<main class="grow" role="content">
  <div class="kt-container-fixed dashboard-wide-container py-5 lg:py-7.5 flex flex-col gap-5">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl font-semibold text-mono">Pending Items</h1>
        @if (alertDateLabel()) {
          <p class="text-sm text-secondary-foreground">
            Pending items since {{ alertDateLabel() }}
          </p>
        } @else {
          <p class="text-sm text-secondary-foreground">
            Items awaiting business information
          </p>
        }
      </div>

      <button
        type="button"
        class="kt-btn kt-btn-outline"
        (click)="onRetry()"
        [disabled]="isLoading()"
      >
        <i class="ki-filled ki-arrows-circle text-sm" aria-hidden="true"></i>
        <span>Refresh</span>
      </button>
    </div>

    @if (isLoading()) {
      <div class="kt-card">
        <div class="kt-card-content p-0">
          <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading pending items">
            <span class="catalog-loading-spinner" aria-hidden="true"></span>
            <span class="catalog-loading-text">Loading...</span>
          </div>
        </div>
      </div>
    }

    @if (errorMessage() && !isLoading()) {
      <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
        <span>{{ errorMessage() }}</span>
        <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">Retry</button>
      </div>
    }

    @if (!isLoading() && !errorMessage()) {
      <div class="dashboard-kpi-grid">
        @for (userCard of userCards(); track trackByUserCard($index, userCard)) {
          <button
            type="button"
            class="dashboard-kpi-card"
            [class.dashboard-kpi-card-alert]="userCard.count > 0"
            [class.dashboard-kpi-card-clear]="userCard.count === 0"
            [class.dashboard-kpi-card-active]="isActiveUserCard(userCard.key)"
            [class.dashboard-kpi-card-all-users]="userCard.isAllUsers"
            [attr.title]="userCard.isAllUsers ? 'Show all pending items' : 'Show pending items for this user'"
            [attr.aria-pressed]="isActiveUserCard(userCard.key)"
            (click)="onSelectUserCard(userCard)"
          >
            <span class="dashboard-kpi-icon" aria-hidden="true">
              <i [class]="userCard.isAllUsers ? 'ki-filled ki-profile-user' : 'ki-filled ki-user'"></i>
            </span>
            <span class="dashboard-kpi-body">
              <span class="dashboard-kpi-count">{{ userCard.count }}</span>
              <span class="dashboard-kpi-label">{{ userCard.label }}</span>
              <span class="dashboard-kpi-hint">
                {{ userCard.count === 0 ? 'All clear' : 'Total pending items' }}
              </span>
            </span>
          </button>
        }
      </div>

      @if (!hasAnyPendingItem()) {
        <div class="kt-card">
          <div class="kt-card-content flex flex-col items-center gap-3 py-12">
            <span class="dashboard-empty-icon" aria-hidden="true">
              <i class="ki-filled ki-check-circle"></i>
            </span>
            <p class="text-base font-semibold text-mono">Nothing pending</p>
            <p class="text-sm text-secondary-foreground">
              All securities have complete business information.
            </p>
          </div>
        </div>
      } @else {
        <div class="kt-card">
          <div class="dashboard-selected-user">
            <span class="dashboard-selected-user-label">Viewing:</span>
            <span class="dashboard-selected-user-value">{{ selectedUserLabel() }}</span>
          </div>
          <div class="kt-card-header min-h-16 flex-wrap gap-3 p-0">
            <div class="dashboard-tabs" role="tablist" aria-label="Pending item categories">
              @for (category of categories(); track trackByCategory($index, category)) {
                <button
                  type="button"
                  role="tab"
                  class="dashboard-tab"
                  [class.dashboard-tab-active]="isActiveCategory(category.key)"
                  [disabled]="category.count === 0"
                  [attr.aria-selected]="isActiveCategory(category.key)"
                  (click)="onSelectCategory(category)"
                >
                  <span>{{ category.label }}</span>
                  <span class="dashboard-tab-badge">{{ category.count }}</span>
                </button>
              }
            </div>
          </div>

          <div class="kt-card-content p-0">
            @for (renderKey of [datatableRenderKey()]; track renderKey) {
            <!-- New Securities -->
            @if (isActiveCategory('newItems')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search new securities..."
                  data-kt-datatable-search="#kt_dashboard_new_items_datatable"
                  aria-label="Search new securities"
                />
              </div>
              <div
                id="kt_dashboard_new_items_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Security/Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeType"><span class="kt-table-col"><span class="kt-table-col-label">Trade Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="securityType"><span class="kt-table-col"><span class="kt-table-col-label">Security Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (newItems().length === 0) {
                        <tr><td colspan="6" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of newItems(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (resolveTypedLink(item).kind !== 'none') {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="linkCommands(resolveTypedLink(item))">{{ typedLinkLabel(item) }}</a>
                              } @else {
                                {{ typedLinkLabel(item) }}
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.tradeType) }}</td>
                            <td>{{ display(item.securityType) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Bonds -->
            @if (isActiveCategory('pendingInitReopenBonds')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search bonds..."
                  data-kt-datatable-search="#kt_dashboard_bonds_datatable"
                  aria-label="Search pending bonds"
                />
              </div>
              <div
                id="kt_dashboard_bonds_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security ID</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="leads"><span class="kt-table-col"><span class="kt-table-col-label">Leads</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="investorAllocation"><span class="kt-table-col"><span class="kt-table-col-label">Investor Allocation</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="type"><span class="kt-table-col"><span class="kt-table-col-label">Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="fndCost"><span class="kt-table-col"><span class="kt-table-col-label">FND Cost</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (initReopenBonds().length === 0) {
                        <tr><td colspan="9" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of initReopenBonds(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (item.tradeId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondLink(item.tradeId)">{{ item.tradeId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td class="dashboard-id-cell">
                              @if (item.securityId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondDefinitionLink(item.securityId)">{{ item.securityId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.leads) }}</td>
                            <td>{{ display(item.investorAllocation) }}</td>
                            <td>{{ display(item.type) }}</td>
                            <td>{{ display(item.fndCost) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Discount Notes -->
            @if (isActiveCategory('pendingDiscountNotes')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search discount notes..."
                  data-kt-datatable-search="#kt_dashboard_discount_notes_datatable"
                  aria-label="Search pending discount notes"
                />
              </div>
              <div
                id="kt_dashboard_discount_notes_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security ID</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="investorAllocation"><span class="kt-table-col"><span class="kt-table-col-label">Investor Allocation</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (discountNotes().length === 0) {
                        <tr><td colspan="6" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of discountNotes(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (item.tradeId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondLink(item.tradeId)">{{ item.tradeId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td class="dashboard-id-cell">
                              @if (item.securityId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondDefinitionLink(item.securityId)">{{ item.securityId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.investorAllocation) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Swaps -->
            @if (isActiveCategory('pendingSwaps')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search swaps..."
                  data-kt-datatable-search="#kt_dashboard_swaps_datatable"
                  aria-label="Search pending swaps"
                />
              </div>
              <div
                id="kt_dashboard_swaps_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="rfrTradeId"><span class="kt-table-col"><span class="kt-table-col-label">RFR Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="structureId"><span class="kt-table-col"><span class="kt-table-col-label">Structure Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="purpose"><span class="kt-table-col"><span class="kt-table-col-label">Purpose</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="purposeSubtype"><span class="kt-table-col"><span class="kt-table-col-label">Purpose Subtype</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (swaps().length === 0) {
                        <tr><td colspan="8" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of swaps(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (item.tradeId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="swapLink(item.tradeId)">{{ item.tradeId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.rfrTradeId) }}</td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td class="dashboard-id-cell">{{ display(item.structureId) }}</td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.purpose) }}</td>
                            <td>{{ display(item.purposeSubtype) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Repurchases -->
            @if (isActiveCategory('pendingRepurcBonds')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search repurchases..."
                  data-kt-datatable-search="#kt_dashboard_repurchases_datatable"
                  aria-label="Search pending repurchases"
                />
              </div>
              <div
                id="kt_dashboard_repurchases_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security ID</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="repurch"><span class="kt-table-col"><span class="kt-table-col-label">Gain/Loss Repurch in USD eq.</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="refinance"><span class="kt-table-col"><span class="kt-table-col-label">Gain/Loss Refinance in USD eq.</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="spread"><span class="kt-table-col"><span class="kt-table-col-label">US3mL Spread for Repurch</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (repurchases().length === 0) {
                        <tr><td colspan="8" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of repurchases(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (item.tradeId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondLink(item.tradeId)">{{ item.tradeId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td class="dashboard-id-cell">
                              @if (item.securityId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondDefinitionLink(item.securityId)">{{ item.securityId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.repurch) }}</td>
                            <td>{{ display(item.refinance) }}</td>
                            <td>{{ display(item.spread) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Bond Definitions -->
            @if (isActiveCategory('pendingBondDefinitions')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search bond definitions..."
                  data-kt-datatable-search="#kt_dashboard_bond_definitions_datatable"
                  aria-label="Search pending bond definitions"
                />
              </div>
              <div
                id="kt_dashboard_bond_definitions_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="documentation"><span class="kt-table-col"><span class="kt-table-col-label">Documentation</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="proceeds"><span class="kt-table-col"><span class="kt-table-col-label">Proceeds</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="format"><span class="kt-table-col"><span class="kt-table-col-label">Format</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (bondDefinitions().length === 0) {
                        <tr><td colspan="7" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of bondDefinitions(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (item.securityId) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="bondDefinitionLink(item.securityId)">{{ item.securityId }}</a>
                              } @else {
                                —
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.documentation) }}</td>
                            <td>{{ display(item.proceeds) }}</td>
                            <td>{{ display(item.format) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Back Office -->
            @if (isActiveCategory('pendingBackOffices')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search back office items..."
                  data-kt-datatable-search="#kt_dashboard_back_office_datatable"
                  aria-label="Search back office pending items"
                />
              </div>
              <div
                id="kt_dashboard_back_office_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Security/Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="type"><span class="kt-table-col"><span class="kt-table-col-label">Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="bondPricingSupplement"><span class="kt-table-col"><span class="kt-table-col-label">Series Number</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tranche"><span class="kt-table-col"><span class="kt-table-col-label">Tranche</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="hasLegalDocument"><span class="kt-table-col"><span class="kt-table-col-label">Legal Document</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="hasTermSheet"><span class="kt-table-col"><span class="kt-table-col-label">Term Sheet</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="hasRta"><span class="kt-table-col"><span class="kt-table-col-label">RTA</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (backOffices().length === 0) {
                        <tr><td colspan="10" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of backOffices(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (backOfficeLink(item).kind !== 'none') {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="linkCommands(backOfficeLink(item))">{{ backOfficeIdLabel(item) }}</a>
                              } @else {
                                {{ backOfficeIdLabel(item) }}
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ backOfficeDealId(item) }}</td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ backOfficeType(item) }}</td>
                            <td>{{ backOfficeSeriesNumber(item) }}</td>
                            <td>{{ backOfficeTranche(item) }}</td>
                            <td>{{ backOfficeLegalDocument(item) }}</td>
                            <td>{{ backOfficeTermSheet(item) }}</td>
                            <td>{{ backOfficeRta(item) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }

            <!-- Canceled Bonds -->
            @if (isActiveCategory('noVerifiedBonds')) {
            <section class="dashboard-panel">
              <div class="dashboard-panel-toolbar">
                <input
                  type="text"
                  class="kt-input sm:w-72"
                  placeholder="Search canceled bonds..."
                  data-kt-datatable-search="#kt_dashboard_canceled_bonds_datatable"
                  aria-label="Search canceled bonds"
                />
              </div>
              <div
                id="kt_dashboard_canceled_bonds_datatable"
                class="kt-card-table dashboard-compact-table"
                data-kt-datatable="true"
                data-kt-datatable-page-size="10"
                data-kt-datatable-state-save="false"
              >
                <div class="kt-table-wrapper kt-scrollable">
                  <table class="kt-table" data-kt-datatable-table="true">
                    <thead>
                      <tr>
                        <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Security/Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="tradeType"><span class="kt-table-col"><span class="kt-table-col-label">Trade Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="securityType"><span class="kt-table-col"><span class="kt-table-col-label">Security Type</span><span class="kt-table-col-sort"></span></span></th>
                        <th scope="col" data-kt-datatable-column="doneUser"><span class="kt-table-col"><span class="kt-table-col-label">TMS User</span><span class="kt-table-col-sort"></span></span></th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (canceledBonds().length === 0) {
                        <tr><td colspan="6" class="dashboard-empty-cell">No pending items.</td></tr>
                      } @else {
                        @for (item of canceledBonds(); track trackByRow($index, item)) {
                          <tr>
                            <td class="dashboard-id-cell">
                              @if (resolveTypedLink(item).kind !== 'none') {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener" [routerLink]="linkCommands(resolveTypedLink(item))">{{ typedLinkLabel(item) }}</a>
                              } @else {
                                {{ typedLinkLabel(item) }}
                              }
                            </td>
                            <td class="dashboard-id-cell">{{ display(item.dealId) }}</td>
                            <td>{{ formatDate(item.tradeDate) }}</td>
                            <td>{{ display(item.tradeType) }}</td>
                            <td>{{ display(item.securityType) }}</td>
                            <td class="dashboard-user-cell">{{ display(item.doneUser) }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="dashboard-panel-footer">
                  <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                  <div class="kt-datatable-length flex items-center gap-3">
                    <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                    <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                  </div>
                </div>
              </div>
            </section>
            }
            }
          </div>
        </div>
      }
    }
  </div>
</main>
